<template>
  <div
    class="black-newsletter-form-widget__wrapper"
    :class="dynamicClass"
    :style="dynamicStyle"
    :data-ref="$options.consts.REFS.ARTICLE_PAGE_NEWSLETTER_WIDGET"
  >
    <div
      :class="$_ampClass"
      class="black-newsletter-form-widget__collapsed-header"
    >
      <div
        v-if="!isWidgetExpanded && !isFormVisible"
        class="black-newsletter-form-widget__collapsed-title-wrapper"
      >
        <button
          class="black-newsletter-form-widget__collapsed-title"
          @click="changeExpandedState"
        >
          Subscribe to Our Exclusive Newsletter!
          <i class="black-newsletter-form-widget__right-arrow">&rarr;</i>
        </button>
      </div>
      <a-wait-for-element-spinner
        v-else-if="isFormVisible"
        :selector="$options.consts.formSelector"
        class="black-newsletter-form-widget__spinner"
        @input="onChangeLoadStatus"
      />
      <div class="black-newsletter-form-widget__expand-button-wrapper">
        <button
          class="black-newsletter-form-widget__up-arrow"
          @click="changeExpandedState"
        />
      </div>
    </div>

    <black-newsletter-form
      v-if="isFormVisible"
      ref="form"
      class="black-newsletter-form-widget__form"
      @submitted="collapseWidget"
      @open-sign-in-form="$emit('open-sign-in-form')"
      @open-sign-up-form="$emit('open-sign-up-form')"
    />
    <div v-if="isFormVisible" class="overlay" @click="collapseWidget" />
  </div>
</template>

<script>
import { PROP_TYPES, propValidator } from '@/utils/validators'
import mixins from '@/utils/mixins'
import { hydrationHelpers } from '@/utils/mixins/hydrationHelpers'
import AWaitForElementSpinner from 'shared/AWaitForElementSpinner'
import { hydrateWhenIdle } from '@/utils/helpers/vue-lazy-hydration/LazyHydrate'
import { waitForElAppearsInDom } from '@fmpedia/helpers'
import { EXPAND_NEWSLETTER_WIDGET_EVENT } from './enums'
import { REFS } from 'enums/external-refs'

const COLLAPSED_WIDGET_HEIGHT = 57
const GOOGLE_CAPTCHA_BOTTOM_MARGIN = 21
const CSS_ANIMATION_DELAY = 300

export default {
  name: 'BlackNewsletterFormWidget',
  mixins: [hydrationHelpers, mixins.pendingAuthActions, mixins.ampClass],
  components: {
    AWaitForElementSpinner,
    BlackNewsletterForm: hydrateWhenIdle(() => import('./BlackNewsletterForm'))
  },
  props: {
    wasExpanded: propValidator([PROP_TYPES.BOOLEAN], false, false)
  },
  consts: {
    formSelector: '.black-newsletter-form__wrapper',
    REFS
  },
  data() {
    return {
      isWidgetExpanded: false,
      isFormLoaded: this.wasExpanded,
      isFormVisible: false,
      formHeight: 'auto',
      prevScrollPosition: null,
      currentScrollPosition: null,
      isScrollingTop: false,
      throttledOnScrollFunc: null
    }
  },
  computed: {
    dynamicClass() {
      return {
        expanded: this.isWidgetExpanded,
        visible: this.isFormVisible
      }
    },
    dynamicStyle() {
      if (!this.isWidgetExpanded || !this.isFormLoaded) {
        return { height: `${COLLAPSED_WIDGET_HEIGHT}px` }
      }

      const units = this.formHeight === 'auto' ? '' : 'px'
      const widgetHeight =
        this.formHeight === 'auto' ? 'auto' : this.calculateWidgetHeight()

      return {
        height: `${widgetHeight}${units}`
      }
    }
  },
  methods: {
    changeExpandedState() {
      if (!this.isWidgetExpanded) {
        this.expandWidget()
      } else {
        this.collapseWidget()
      }
    },
    async expandWidget() {
      this.isFormVisible = true
      const isFormLoaded = await waitForElAppearsInDom({
        selector: this.$options.consts.formSelector,
        timeout: 30000
      })

      if (!isFormLoaded) return

      this.$nextTick(() => {
        this.formHeight = this.getFormHeight()
        setTimeout(() => {
          this.formHeight = 'auto'
        }, CSS_ANIMATION_DELAY)
        this.isWidgetExpanded = true
        this.$emit('update:wasExpanded', true)
      })
    },
    collapseWidget() {
      this.formHeight = this.getFormHeight()
      this.isFormVisible = false
      this.$nextTick(() => {
        this.isWidgetExpanded = false
      })
    },
    getFormHeight() {
      const form = document.querySelector(this.$options.consts.formSelector)

      if (!form) return 0

      return form.clientHeight
    },
    calculateWidgetHeight() {
      return (
        this.formHeight + COLLAPSED_WIDGET_HEIGHT + GOOGLE_CAPTCHA_BOTTOM_MARGIN
      )
    },
    onChangeLoadStatus(isLoaded) {
      if (isLoaded) {
        this.isFormLoaded = isLoaded
      } else {
        this.collapseWidget()
      }
    }
  },
  mounted() {
    this.$bus.$on(EXPAND_NEWSLETTER_WIDGET_EVENT, this.expandWidget)
  },
  beforeDestroy() {
    this.$bus.$off(EXPAND_NEWSLETTER_WIDGET_EVENT, this.expandWidget)
  }
}
</script>

<style scoped lang="scss">
@import '~assets/scss/local/mixins/grid';
@import '~assets/scss/local/styles/article';

/* This delay is also used in JS - CSS_ANIMATION_DELAY enum
 * Do not forget to update both: CSS and JS values
 */
$height-animation-delay: 0.3s;

.black-newsletter-form-widget__wrapper {
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  background: $c--black;
  color: $c--white;
  transition: height $height-animation-delay ease-in-out;
  z-index: $z-index-article-newsletter-widget;

  .black-newsletter-form-widget__collapsed-header {
    $grid-setup: (
      $columnSetup: (
        $mobile: minmax(0, 100%) 150px minmax(0, 100%)
      )
    );
    @include gridBuilder($grid-setup, $handle-amp: true);

    height: 57px;
  }

  .black-newsletter-form-widget__collapsed-title-wrapper {
    display: flex;
    justify-content: center;
    align-items: center;
    grid-area: 1 / 2 / 2 / 3;
  }

  .black-newsletter-form-widget__collapsed-title {
    position: relative;
    text-align: center;
    color: $c--white;

    @extend .newsletter-title-collapsed;
  }

  .black-newsletter-form-widget__right-arrow {
    position: absolute;
    right: -10px;
    bottom: 0;
    font-size: 22px;
    line-height: 22px;
  }

  .black-newsletter-form-widget__expand-button-wrapper {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    grid-area: 1 / 3 / 2 / 4;
  }

  .black-newsletter-form-widget__up-arrow {
    content: '';
    width: 10px;
    height: 10px;
    margin-right: 23px;
    border-left: 2px solid $c--white;
    border-top: 2px solid $c--white;
    transform: rotate(45deg);
  }

  &.expanded .black-newsletter-form-widget__up-arrow {
    transform: rotate(225deg);
  }

  .black-newsletter-form-widget__spinner {
    margin: 0 auto;
    grid-area: 1 / 2 / 2 / 3;
  }

  .black-newsletter-form-widget__modal-widget {
    height: 100%;
  }

  .black-newsletter-form-widget__form {
    visibility: hidden;
  }

  &.visible .black-newsletter-form-widget__form {
    visibility: visible;
  }

  .overlay {
    position: absolute;
    opacity: 0;
    background: yellow;
    bottom: 100%;
    left: 0;
    height: 100vh;
    width: 100%;
  }
}
</style>
